import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

export default function ChartBar({
  servicesDetails,
  data,
  maxPercentage = 100,
  arPhrase,
  enPhrase,
  isSubChart,
}) {
  const lang =
    typeof window !== "undefined" ? window.localStorage.getItem("lang") : "ar";

  const [isShown, setIsShown] = useState(false);
  const [title, setTitle] = useState("");
  const [totalCount, setTotalCount] = useState("");
  const [sub, setSub] = useState([]);

  const colorPaletteLatest = [
    {
      caseStatusNameLatest: "قيد المراجعة",
      caseStatusNameLatestEn: "Pending",
      colorLatest: "#CCDBFA",
      id: 1,
    },
    {
      caseStatusNameLatest: "مطلوب بيانات اضافية",
      caseStatusNameLatestEn: "Need More Data",
      colorLatest: "#EAD1F9",
      id: 2,
    },
    {
      caseStatusNameLatest: "قيد السداد",
      caseStatusNameLatestEn: "Pending for Payment",
      colorLatest: "#F9E5CF",
      id: 3,
    },
    {
      caseStatusNameLatest: "قيد التنفيذ",
      caseStatusNameLatestEn: "In Progress",
      colorLatest: "#ECEDD0",
      id: 4,
    },
    {
      caseStatusNameLatest: "منتهية",
      caseStatusNameLatestEn: "Ended",
      colorLatest: "#C8E5DD",
      id: 5,
    },
    {
      caseStatusNameLatest: "يتعذر التنفيذ",
      caseStatusNameLatestEn: "Unable to Progress",
      colorLatest: "#D4D2CE",
      id: 6,
    },
    {
      caseStatusNameLatest: "وقوف مؤقت",
      caseStatusNameLatestEn: "Partially Stopped",
      colorLatest: "#FFB9AD",
      id: 7,
    },
    {
      caseStatusNameLatest: "ملغي",
      caseStatusNameLatestEn: "Cancelled",
      colorLatest: "#FF5353",
      id: 8,
    },
  ];

  const getStatusColor = (id) => {
    if (id === 1) return "#CCDBFA";
    if (id === 2) return "#EAD1F9";
    if (id === 3) return "#F9E5CF";
    if (id === 4) return "#ECEDD0";
    if (id === 5) return "#C8E5DD";
    if (id === 6) return "#D4D2CE";
    if (id === 7) return "#FFB9AD";
    if (id === 8) return "#FF5353";
    return "";
  };

  const categories = servicesDetails?.servicesChart?.[0]?.categories ?? [];

  const getCategoryTotal = (cat) =>
    (cat?.statuses ?? []).reduce((sum, s) => sum + (Number(s?.count) || 0), 0);

  const { maxCategoryTotal, maxStatusCount } = useMemo(() => {
    const maxCat = Math.max(1, ...categories.map(getCategoryTotal));

    const maxStatus = Math.max(
      1,
      ...categories.flatMap((c) =>
        (c?.statuses ?? []).map((s) => Number(s?.count) || 0),
      ),
    );

    return { maxCategoryTotal: maxCat, maxStatusCount: maxStatus };
  }, [categories]);

  const niceStep = (raw) => {
    if (raw <= 1) return 1;
    if (raw <= 2) return 2;
    if (raw <= 5) return 5;
    if (raw <= 10) return 10;
    return Math.ceil(raw / 10) * 10;
  };

  const yStep = useMemo(() => {
    const rawStep = maxCategoryTotal / 5;
    return niceStep(rawStep);
  }, [maxCategoryTotal]);

  const yMax = yStep * 5;

  // =========================
  // MOBILE WIDTH GUARANTEE
  // =========================
  // We measure the available row width and distribute segments proportionally
  // so SUM(segments) <= availableWidth (never exceeds).
  const mobileBarRowRef = useRef(null);
  const [mobileBarRowWidth, setMobileBarRowWidth] = useState(0);

  useLayoutEffect(() => {
    if (!mobileBarRowRef.current) return;

    const el = mobileBarRowRef.current;

    const update = () => setMobileBarRowWidth(el.clientWidth || 0);

    update();

    const ro = new ResizeObserver(update);
    ro.observe(el);

    window.addEventListener("resize", update);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  // If you want tiny values visible, you can set MIN_SEGMENT_PX = 1 or 2.
  // Keeping it 0 is the safest to avoid any forced overflow with many statuses.
  const MIN_SEGMENT_PX = 0;

  const getMobileSegmentWidthPx = (count, categoryTotal) => {
    const v = Number(count) || 0;
    if (v <= 0) return 0;

    const total = Math.max(1, Number(categoryTotal) || 0);
    const available = Math.max(0, mobileBarRowWidth || 0);

    // proportional distribution inside available width
    const w = (v / total) * available;

    // clamp to avoid any rounding causing overflow
    return Math.round(clamp(w, MIN_SEGMENT_PX, available));
  };

  // =========================
  // DESKTOP HEIGHT GUARANTEE
  // =========================
  const DESKTOP_CHART_MAX_PX = 210;

  const getDesktopSegmentHeightPx = (count, categoryTotal) => {
    const v = Number(count) || 0;
    if (v <= 0) return 0;

    const catTotal = Math.max(1, Number(categoryTotal) || 0);

    const categoryScale = catTotal / yMax; // yMax is nice axis max
    const stackHeight = categoryScale * DESKTOP_CHART_MAX_PX;

    const segmentRatioInStack = v / catTotal;

    const min = 2;
    const h = segmentRatioInStack * stackHeight;

    // clamp to chart max height to guarantee not exceeding
    return Math.round(clamp(h, min, DESKTOP_CHART_MAX_PX));
  };

  const statusChartBarHAndler = (item) => {
    setIsShown(item);
    setTitle(item?.name || "");
    setTotalCount(item?.count || "");
    setSub(item?.statuses || []);
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 1024;

  return (
    <div>
      {isMobile ? (
        <div className="w-full">
          {categories?.map((item, index) => {
            const categoryTotal = getCategoryTotal(item);

            return (
              <div className="mb-[15px]" key={index}>
                <div className="flex items-center justify-between w-full">
                  <p className="text-[12px] font-Almarai text-[#000] h-[13px] flex items-center gap-[2px]">
                    {item?.name}({item?.count})
                  </p>

                  <div className="flex items-center gap-[12px] w-full justify-end h-[16px] !w-fit">
                    {(item?.statuses ?? []).map((st, i) => (
                      <div key={i} className="flex items-center gap-[4px]">
                        <div
                          className="rounded-[50%] w-[6px] h-[6px]"
                          style={{ background: getStatusColor(st?.id) }}
                        />
                        <span className="text-[10px] font-Almarai">
                          {st?.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Mobile bars (guaranteed not exceed) */}
                <div className="mt-[8px] flex justify-end">
                  <div
                    ref={mobileBarRowRef}
                    className="flex gap-[1px] items-center justify-end w-full max-w-full overflow-hidden"
                  >
                    {(item?.statuses ?? []).map((st, i) => (
                      <div
                        key={i}
                        className="rounded-[3px] h-[10px] mt-[6px] shrink-0"
                        style={{
                          background: getStatusColor(st?.id),
                          width: `${getMobileSegmentWidthPx(
                            st?.count,
                            categoryTotal,
                          )}px`,
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Legend */}
          <div className="flex items-start gap-[8px] mt-6 justify-center w-full flex-wrap">
            {colorPaletteLatest?.map((item, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                  marginBottom: "8px",
                }}
              >
                <span
                  className="w-[9px] h-[9px] rotate-45"
                  style={{ background: item?.colorLatest }}
                />
                <p className="text-[12px] md:text-[10px] font-bold">
                  {lang === "en"
                    ? item?.caseStatusNameLatestEn
                    : item?.caseStatusNameLatest}
                </p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div>
          <div
            className={`w-full min-w-400 ${isSubChart ? "px-[24px]" : ""} bg-white relative`}
            style={{ height: 42 * 6 }} // 6 rows * 42px
          >
            {/* Grid lines */}
            {[5, 4, 3, 2, 1, 0].map((n, idx) => (
              <div key={idx} className="flex items-end gap-3 h-[42px]">
                <span className="w-5 leading-4 text-xs">{n * yStep}</span>
                <div className="bg-transparent h-[30px] w-full border-[#EEE2D0] border-b-2" />
              </div>
            ))}

            {/* Bars */}
           <div className="absolute top-0 right-2 left-2 w-full h-full flex items-end overflow-visible">
              {categories?.map((item, index) => {
                const categoryTotal = getCategoryTotal(item);

                return (
                  <div
                    key={index}
                    className="flex flex-col items-end md:items-center max-h-[210px] truncate"
                    style={{
                      width: `${95 / (categories?.length || 1)}%`,
                    }}
                    onMouseEnter={() => statusChartBarHAndler(item)}
                    onMouseLeave={() => setIsShown(false)}
                  >
                    {/* Tooltip */}
                    <div
                      className={`absolute top-[-25px] border-[1px] border-solid border-[#eee2d0] rounded-[8px] ${
                        isShown === item ? "flex" : "hidden"
                      } flex-col w-[235px] bg-[#fff] px-[16px] pt-[8px] py-[12px] shadow-[0_4px_16px_0_rgba(0,0,0,0.3)]`}
                    >
                      <div className="flex items-center justify-between gap-[28px]">
                        <p className="text-[12px] text-[#010202] font-Almarai">
                          {title}
                        </p>
                        <p className="text-[12px] text-[#010202] font-Almarai">
                          {totalCount}
                        </p>
                      </div>

                      <div className="w-[185px] h-[1px] bg-[#eee2d0] mt-[7.5px] mb-[11.5px]" />

                      <div>
                        {(sub ?? []).map((status, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between gap-[30px] mb-[12px]"
                          >
                            <div className="flex items-center justify-center gap-[8px]">
                              <div
                                className="h-[14px] w-[4px]"
                                style={{
                                  background: getStatusColor(status?.id),
                                }}
                              />
                              <p className="text-[12px] text-[#010202] font-Almarai font-bold">
                                {status?.name}
                              </p>
                            </div>

                            <div className="flex items-center justify-center gap-[3px]">
                              <p className="text-[12px] text-[#010202] font-Almarai font-bold">
                                {status?.count}
                              </p>
                              <p className="text-[12px] text-[#949494] font-Almarai">
                                {lang === "en"
                                  ? enPhrase?.["lblCase"] || "Case"
                                  : arPhrase?.["lblCase"] || "قضية"}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Stacked segments */}
                    {(item?.statuses ?? []).map((itemColor, i) => (
                      <div
                        key={i}
                        className="rounded-sm w-2 md:w-4"
                        style={{
                          background: getStatusColor(itemColor?.id),
                          height: `${getDesktopSegmentHeightPx(
                            itemColor?.count,
                            categoryTotal,
                          )}px`,
                        }}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* X labels */}
          <div className="flex items-center justify-start mt-[16px] w-full ms-[10px]">
            {categories?.map((ser, index) => (
              <div
                key={index}
                style={{
                  width: `${95 / (categories?.length || 1)}%`,
                  maxWidth: `${95 / (categories?.length || 1)}%`,
                }}
              >
                <p className="text-[8px] lg:text-xs text-center">{ser?.name}</p>
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-start gap-3 mt-10 justify-center w-full flex-wrap">
            {colorPaletteLatest?.map((item, index) => (
              <div
                key={index}
                style={{ display: "flex", gap: "4px", alignItems: "center" }}
              >
                <span
                  className="w-[12px] h-[12px] rotate-45"
                  style={{ background: item?.colorLatest }}
                />
                <p className="text-[8px] md:text-[10px] font-bold">
                  {lang === "en"
                    ? item?.caseStatusNameLatestEn
                    : item?.caseStatusNameLatest}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}